/**
 * @module ToggleRadiosControl
 */
import './ToggleRadiosControl.css';
import { Control } from 'ol/control.js';

/**
 * @description
 * This module extends the Open Layers control class to create a control that
 * toggles between two or more radio buttons. The control is added to the map 
 * in the application code.
 * 
 * Currently, this module is designed to work exclusively with the Rip Current
 * application, but it could be generalized for other applications.
 */

class ToggleRadiosControl extends Control {
  /**
   * @param {Object} [opt_options] Control options.
   */
  constructor(opt_options) {
    const options = opt_options || {};
    // The element that will contain the control needs to be created here
    // and passed to the parent constructor.
    options.element = document.createElement('div');
    super(options);
    
    this.options = options;

    // Create the main wrapper div
    const toggleRadioWrapper = document.createElement('div');
    toggleRadioWrapper.className = 'toggle-radio';
    toggleRadioWrapper.setAttribute('data-style', 'square');
    //toggleRadioWrapper.setAttribute('data-color', 'blue');

    // Create the first radio input and label
    const radio1 = document.createElement('input');
    radio1.type = 'radio';
    radio1.name = 'rip_currents';
    radio1.id = this.options.startLayerId;
    radio1.value = 'mode1';
    radio1.checked = true;

    const label1 = document.createElement('label');
    label1.htmlFor = this.options.startLayerId;
    label1.textContent = this.options.startLayerName;

    // Create the second radio input and label
    const radio2 = document.createElement('input');
    radio2.type = 'radio';
    radio2.name = 'rip_currents';
    radio2.id = this.options.nextLayerId;
    radio2.value = 'mode2';

    const label2 = document.createElement('label');
    label2.htmlFor = this.options.nextLayerId;
    label2.textContent = this.options.nextLayerName;

    // Create the third radio input and label
    const radio3 = document.createElement('input');
    radio3.type = 'radio';
    radio3.name = 'rip_currents';
    radio3.id = this.options.comboLayerId;
    radio3.value = 'mode3';

    const label3 = document.createElement('label');
    label3.htmlFor = this.options.comboLayerId;
    label3.textContent = this.options.comboLayerName;

    // Append the inputs and labels to the wrapper
    toggleRadioWrapper.appendChild(radio1);
    toggleRadioWrapper.appendChild(label1);
    toggleRadioWrapper.appendChild(radio2);
    toggleRadioWrapper.appendChild(label2);
    toggleRadioWrapper.appendChild(radio3);
    toggleRadioWrapper.appendChild(label3);

    // Append the wrapper to the desired parent element
    this.element.appendChild(toggleRadioWrapper);

  }

  setMap(map) {
    // Call the parent method.
    super.setMap(map);
  
    // Ensure the map is not null.
    if (map) {
      // Get references to the radio buttons
      const radio1 = document.getElementById(this.options.startLayerId);
      const radio2 = document.getElementById(this.options.nextLayerId);
      const radio3 = document.getElementById(this.options.comboLayerId);

      if (!radio1 || !radio2) {
        console.error('Radio buttons not found');
        return;
      }
      
      // Ensure the radio buttons are interactive
      radio1.disabled = false;
      radio2.disabled = false;
      radio3.disabled = false;

      // Format the date strings using native JavaScript Date
      const startDateObj = new Date(this.options.startLayerDate);
      const nextDateObj = new Date(this.options.nextLayerDate);

      const fullDateOptions = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
      const monthDayOptions = { month: 'long', day: 'numeric', timeZone: 'UTC' };

      const startDate = startDateObj.toLocaleDateString('en-US', fullDateOptions);
      const nextDate = nextDateObj.toLocaleDateString('en-US', fullDateOptions);

      const compositeDate1 = startDateObj.toLocaleDateString('en-US', monthDayOptions);
      // Manually construct the 'dd, yyyy' format to match the original logic
      const compositeDate2 = `${nextDateObj.getUTCDate()}, ${nextDateObj.getUTCFullYear()}`;
      const compositeDate = `${compositeDate1}-${compositeDate2}`;

      // Define the event handler function
      const handleRadioChange = () => {
        const validForElement = document.getElementById('valid-for');
        const pngDownloadButton = document.getElementById('rip-current-png-download');
        const gisDownloadButton = document.getElementById('rip-current-gis-download');
        const gisFilenameElement = document.getElementById('rip-current-gis-filename');
        if (radio1.checked) {
          this.options.startLayer.setVisible(true);
          this.options.nextLayer.setVisible(false);
          this.options.comboLayer.setVisible(false);
          // set the value of the #valid-for element.
          validForElement.textContent = `Valid for Today (${startDate})`;
          validForElement.setAttribute('data-day', 'today');
          pngDownloadButton.setAttribute('data-filename', 'rip-currents-day-1.png');
          gisDownloadButton.setAttribute('data-filename', '/gis/rip_currents/rip_currents_day1_latest.geojson');
          gisDownloadButton.setAttribute('href', '/gis/rip_currents/rip_currents_day1_latest.geojson');
          gisDownloadButton.setAttribute('download', 'rip_currents_day1_latest.geojson');
          gisFilenameElement.textContent = "(rip_currents_day1_latest.geojson)";
        }
        else if (radio2.checked) {
          this.options.startLayer.setVisible(false);
          this.options.nextLayer.setVisible(true);
          this.options.comboLayer.setVisible(false);
          validForElement.textContent = `Valid for Tomorrow (${nextDate})`;
          validForElement.setAttribute('data-day', 'tomorrow');
          pngDownloadButton.setAttribute('data-filename', 'rip-currents-day-2.png');
          gisDownloadButton.setAttribute('data-filename', '/gis/rip_currents/rip_currents_day2_latest.geojson');
          gisDownloadButton.setAttribute('href', '/gis/rip_currents/rip_currents_day2_latest.geojson');
          gisDownloadButton.setAttribute('download', 'rip_currents_day2_latest.geojson');
          gisFilenameElement.textContent = "(rip_currents_day2_latest.geojson)";
        }
        else if (radio3.checked) {
          this.options.startLayer.setVisible(false);
          this.options.nextLayer.setVisible(false);
          this.options.comboLayer.setVisible(true);
          validForElement.textContent = `Composite data (${compositeDate})`;
          validForElement.setAttribute('data-day', 'combined');
          pngDownloadButton.setAttribute('data-filename', 'rip-currents-combined.png');
          gisDownloadButton.setAttribute('data-filename', '/gis/rip_currents/rip_currents_composite_latest.geojson');
          gisDownloadButton.setAttribute('href', '/gis/rip_currents/rip_currents_composite_latest.geojson');
          gisDownloadButton.setAttribute('download', 'rip_currents_composite_latest.geojson');
          gisFilenameElement.textContent = "(rip_currents_composite_latest.geojson)";
        }
      };

      // Add event listeners to the radio buttons
      radio1.addEventListener('change', handleRadioChange);
      radio2.addEventListener('change', handleRadioChange);
      radio3.addEventListener('change', handleRadioChange);

      // Initial check to set the correct layer based on the default selected radio button
      handleRadioChange();
    }
  }

  /**
   * Updates the language-specific elements.
   */
  updateLanguage() {
  }

}

export default ToggleRadiosControl;